import React, { FC, useCallback, useEffect, useState } from 'react';
import i18n from 'i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useFormContext, Controller, ControllerRenderProps } from 'react-hook-form';
import { Dropdown } from 'react-bootstrap';
import { AppDispatch } from 'store/store';
import { useUncontrolledInput } from 'modules/core/hooks';
import { IFormField } from 'modules/core/types/form';
import { PREFERENCES_PE_FIELD_NAMES } from 'modules/core/shared';
import { getRACState, getTiers } from 'store/selectors/RACAffordabilityEngine.selector';
import { getTierOptionsThunk } from 'actions/controlActions';
import { CreditScoreColors } from 'modules/ProbabilityEstimator/enums/creditScoreColors.enum';
import { getUser } from 'store/selectors/auth.selector';
import { getDealSourceData } from 'store/selectors/RACAffordabilityEngine.selector';
import { SSO_TYPE } from 'modules/core/constants';

import { ITier } from '../../core/types';
import {
  DefaultToggle,
  OptionItem,
  ScoreMenu,
  SelectedScoreToggle,
  CheckmarkIcon,
  OptionContent,
  TextWrapper,
  CheckmarkSpacer,
} from '../../ProbabilityEstimator/CreditScoreDropdown/styles';

export interface ITierDropdownProps extends IFormField {
  isMakeItWork?: boolean;
}

export const TierDropdown: FC<ITierDropdownProps> = ({ name, validate, isMakeItWork = false, disabled, onToggle }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { watch } = useFormContext();
  const { controlField, setValue } = useUncontrolledInput(name);
  const tiers = useSelector(getTiers);
  const user = useSelector(getUser);
  const dealSourceData = useSelector(getDealSourceData);
  const { dealSourceTierEditable } = useSelector(getRACState);
  const selectedOption = watch(name) as ITier;
  const tierDisplayName = (tier: number) => i18n.t('components.creditScore.tier', { tier });
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const isSelectedDefault = selectedOption.color === CreditScoreColors.DEFAULT;
  const ToggleComponent = isSelectedDefault ? DefaultToggle : SelectedScoreToggle;
  const toggleTitle = selectedOption.tier
    ? tierDisplayName(selectedOption.tier)
    : i18n.t('components.creditScore.title');

  const renderOptions = useCallback(
    ({ onChange, onBlur }: ControllerRenderProps) =>
      tiers.map((tierOption) => {
        const isSelectedOption = selectedOption.tier === tierOption.tier;

        return (
          <OptionItem
            data-testid="tier-option"
            onClick={() => {
              onChange(tierOption);
              setValue(PREFERENCES_PE_FIELD_NAMES.LVT_HIGH_LIMIT, tierOption.max_ltv);
            }}
            onBlur={onBlur}
            selected={isSelectedOption}
            key={tierOption.tier}
            color={tierOption.color}
          >
            <OptionContent>
              <TextWrapper>
                {isSelectedOption ? <CheckmarkIcon color={tierOption.color as any} /> : <CheckmarkSpacer />}
                {tierDisplayName(tierOption.tier)}
              </TextWrapper>
            </OptionContent>
          </OptionItem>
        );
      }),
    [selectedOption, setValue, tiers],
  );

  const renderDropdown = useCallback(
    (renderProps) => (
      <Dropdown
        data-testid="tier-control"
        onToggle={(isOpen: boolean) => {
          setIsOpen(isOpen);
          onToggle && onToggle(isOpen);
        }}
      >
        <ToggleComponent
          disabled={(user?.is_sso && user?.sso_type !== SSO_TYPE.DEALER && !dealSourceTierEditable) || disabled}
          color={selectedOption.color}
          $isOpen={isOpen}
          data-testid="default-toggle"
        >
          {toggleTitle}
        </ToggleComponent>
        <ScoreMenu>{renderOptions(renderProps)}</ScoreMenu>
      </Dropdown>
    ),
    [
      ToggleComponent,
      user?.is_sso,
      user?.sso_type,
      dealSourceTierEditable,
      selectedOption.color,
      toggleTitle,
      renderOptions,
      isOpen,
      disabled,
      onToggle,
    ],
  );

  useEffect(() => {
    const params = isMakeItWork ? { deal_date: dealSourceData?.submitted_date_time } : {};

    dispatch(getTierOptionsThunk(params));
  }, [dispatch, dealSourceData?.submitted_date_time, isMakeItWork]);

  return (
    <Controller
      name={name}
      render={renderDropdown}
      control={controlField}
      rules={{
        validate,
      }}
    />
  );
};
